import { ReviewLevel } from '@scale/shared-types';
import { Claim } from '@scale/shared/types/queue';
import { MutableRefObject } from 'react';
import {
  IndexedInteractionStepResponse,
  InteractionStepResponse,
  InteractionStepType,
  MultiTurnInteractionConfiguration,
  StepParamsForTypeWithStage,
  CreateCompletionPair,
} from '@scale/shared/types/chat';
import { StoreApi } from 'zustand';
import type { LintMessage } from '../../ThreadedInteraction/hooks/usePerStepResponseLinter/LintMessage';
import { NormalLintResult, ScriptLinterMetadata } from '@scale/shared/types/linter';
import { PopulatedProjectModelConfigDTO } from '@scale/customer-app-shared';

export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

type Write<T, U> = Omit<T, keyof U> & U;
type Cast<T, U> = T extends U ? T : U;
type TakeTwo<T> = T extends {
  length: 0;
}
  ? [undefined, undefined]
  : T extends {
      length: 1;
    }
  ? [...a0: Cast<T, unknown[]>, a1: undefined]
  : T extends {
      length: 0 | 1;
    }
  ? [...a0: Cast<T, unknown[]>, a1: undefined]
  : T extends {
      length: 2;
    }
  ? T
  : T extends {
      length: 1 | 2;
    }
  ? T
  : T extends {
      length: 0 | 1 | 2;
    }
  ? T
  : T extends [infer A0, infer A1, ...unknown[]]
  ? [A0, A1]
  : T extends [infer A0, (infer A1)?, ...unknown[]]
  ? [A0, A1?]
  : T extends [(infer A0)?, (infer A1)?, ...unknown[]]
  ? [A0?, A1?]
  : never;
export type WithDevtools<S> = Write<S, StoreDevtools<S>>;
type StoreDevtools<S> = S extends {
  setState: (...a: infer Sa) => infer Sr;
}
  ? {
      setState<
        A extends
          | string
          | {
              type: unknown;
            },
      >(
        ...a: [...a: TakeTwo<Sa>, action?: A]
      ): Sr;
    }
  : never;

export type ExtractState<S> = S extends { getState: () => infer X } ? X : never;
export type StoreSubscribeWithSelector<T> = {
  subscribe: {
    (listener: (selectedState: T, previousSelectedState: T) => void): () => void;
    <U>(
      selector: (state: T) => U,
      listener: (selectedState: U, previousSelectedState: U) => void,
      options?: {
        equalityFn?: (a: U, b: U) => boolean;
        fireImmediately?: boolean;
      },
    ): () => void;
  };
};
export type RequiredProps = Pick<ChatStoreProps, 'config'>;
export type ComputedProps = 'stepParams' | 'allStepsCompleted';
export type InitialProps = Prettify<
  Partial<Exclude<ChatStoreProps, ComputedProps>> & RequiredProps
>;
export type ChatStore = Omit<WithDevtools<StoreApi<ChatStoreState>>, 'subscribe'> &
  StoreSubscribeWithSelector<any>;
export type StoreRef = MutableRefObject<ChatStore>;

export type DefaultProps = Prettify<
  Pick<
    ChatStoreProps,
    | 'languageCode'
    | 'responses'
    | 'stepParams'
    | 'allStepsCompleted'
    | 'stepLintersMetadata'
    | 'reviewLevel'
    | 'completedSteps'
    | 'hasActiveStepResponseLinterError'
    | 'invalidStepIndices'
    | 'prefetchingInProgress'
    | 'lintMessages'
    | 'isPerStepLinterValidating'
    | 'perStepLinterParamsAtLastCheck'
    | 'stepWidth'
    | 'currentMultiChatIndex'
    | 'workstreamModels'
    | 'jsonataContextList'
    | 'turnVersionHistory'
    | 'interactionHistory'
    | 'lastTurnWorkStartTime'
    | 'isRealCustomerAudit'
    | 'projectModelConfigurationMapping'
  >
>;

export type ChatStoreMiddlewares = {
  setResponses?: (
    responses: IndexedInteractionStepResponse[],
  ) => Promise<IndexedInteractionStepResponse[]>;
  setConfig?: (
    config: MultiTurnInteractionConfiguration,
  ) => Promise<MultiTurnInteractionConfiguration>;
};

export type StepLinterMetadata = {
  hasLinterError?: boolean;
  hasIncompleteFrontendLinterFeedback?: boolean;
};

export interface ChatStoreProps {
  languageCode: string;
  responses: IndexedInteractionStepResponse[];
  stepParams: StepParamsForTypeWithStage<InteractionStepType>[];
  allStepsCompleted: boolean;
  stepLintersMetadata: StepLinterMetadata[];
  config: MultiTurnInteractionConfiguration;
  reviewLevel: ReviewLevel;
  completedSteps: Record<number, boolean>;
  hasActiveStepResponseLinterError: boolean;
  invalidStepIndices: InvalidStepIndex[];
  prefetchingInProgress: boolean;
  lintMessages: LintMessage[];
  isPerStepLinterValidating: boolean;
  perStepLinterParamsAtLastCheck: any;
  stepWidth: GlobalStepWidth;
  currentMultiChatIndex?: number;
  workstreamModels: Record<string, string[]>;
  jsonataContextList: NormalLintResult<ScriptLinterMetadata<any>>[];

  // A record of turn number to a history of prompt and model response pairs.
  interactionHistory: Record<number, CreateCompletionPair[]>;

  // TODO (Matthew / Lajos): Deprecate in favor of emitting a clear local state event
  // This is temporary to forcibly trigger remounts when we restart the turn and need to clear the local state of the first turn step
  turnVersionHistory: Record<number, number>;
  isRealCustomerAudit?: boolean;
  customerAuditShowInstructions?: boolean;
  assignmentId?: string;
  claim?: Claim;
  lastTurnWorkStartTime: number;

  // A populated record of projectModelConfigurationKey to a model configuration to be consistent across the task.
  projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>;
}

export enum GlobalStepWidth {
  Full = 'full',
  Dynamic = 'dynamic',
}

export interface InvalidStepIndex {
  stepIndex: number;
  errorMessage: string;
  turnIndex?: number;
}

export interface ChatStoreState extends ChatStoreProps {
  setJsonataContextList: (value: any) => void;
  setResponses: (responses: IndexedInteractionStepResponse[]) => void;
  atomicallySetIndexConfigAndResponsesNoMiddlewareOnly: (
    index: number,
    config: MultiTurnInteractionConfiguration,
    responses: IndexedInteractionStepResponse[],
  ) => void;
  setStepLintersMetadata: (stepLintersMetadata: StepLinterMetadata[]) => void;
  setHasLinterErrorForIndex: (index: number, value: boolean) => void;
  setHasIncompleteFrontendLinterFeedbackForIndex: (index: number, value: boolean) => void;
  clearStepLinterErrors: () => void;
  setCompletedSteps: (
    callback: (previousCompletedSteps: Record<number, boolean>) => Record<number, boolean>,
  ) => void;
  setResponsesAndCompletedSteps: (responses: IndexedInteractionStepResponse[]) => void;
  setMultiTab: (
    reviewLevel: ReviewLevel,
    currentMultiChatIndex: number,
    responses: IndexedInteractionStepResponse[],
  ) => void;
  markStepAsCompletedByIndex: (index: number) => void;
  markStepAsIncompletedByIndex: (index: number) => void;
  batchUpdateCompletedSteps: (
    incompletedStepIndices: number[],
    completedStepIndices: number[],
  ) => void;
  setConfig: (config: MultiTurnInteractionConfiguration) => void;
  setReviewLevel: (reviewLevel: ReviewLevel) => void;
  setHasActiveStepResponseLinterError: (value: boolean) => void;
  setInvalidStepIndices: (indexObject: InvalidStepIndex[]) => void;
  setPrefetchingInProgress: (prefetchingInProgress: boolean) => void;
  setResponseForIndex: (
    response: InteractionStepResponse,
    index: number,
    shouldResetHistory?: boolean,
  ) => void;
  setLintMessages: (value: LintMessage[] | ((prev: LintMessage[]) => LintMessage[])) => void;
  setIsPerStepLinterValidating: (value: boolean) => void;
  setPerStepLinterParamsAtLastCheck: (value: any) => void;
  setStepWidth: (stepWidth: GlobalStepWidth) => void;
  setCurrentMultiChatIndex: (index: number) => void;
  setWorkstreamModels: (workstreamModels: Record<string, string[]>) => void;
  setInteractionHistory: (interactionHistory: Record<number, CreateCompletionPair[]>) => void;
  setIsRealCustomerAudit: (isRealCustomerAudit: boolean) => void;
  setLastTurnWorkStartTime: (lastTurnWorkStartTime: number) => void;
  setProjectModelConfigurationMapping: (
    projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
  ) => void;
}
